import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";

import { getAllCandidates } from "../../Api/candidateApi";
import dbskillsLogo from "../../Assets/dbskills-logo.png";
import skillIndiaLogo from "../../Assets/Skill-India_Preview.png";
import shriramFinanceLogo from "../../Assets/Shriram-Finance-Logo.png";

const FILE_BASE_URL = "https://api2.eduforestin.com";

const getFileUrl = (value) => {
  if (!value) return "";
  if (typeof value !== "string") return "";
  if (
    value.startsWith("data:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }
  const cleanPath = value.replace(/^\/+/, "");
  return `${FILE_BASE_URL}/${cleanPath}`;
};

const formatDateDisplay = (dateVal) => {
  if (!dateVal) return "";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal);
    return d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .toUpperCase();
  } catch (e) {
    return String(dateVal);
  }
};

const getExportFilename = (locationFilter, fromDate, toDate, extension) => {
  let locName = "All_Locations";
  if (
    locationFilter &&
    locationFilter !== "ALL" &&
    locationFilter !== "All Locations" &&
    locationFilter.trim() !== ""
  ) {
    locName = locationFilter.trim().replace(/\s+/g, "_");
  }

  let datePart = "";
  if (fromDate && toDate) {
    datePart = `_${fromDate}_to_${toDate}`;
  } else if (fromDate) {
    datePart = `_${fromDate}`;
  } else if (toDate) {
    datePart = `_to_${toDate}`;
  }

  return `Candidates_${locName}${datePart}.${extension}`;
};

export default function ExportCandidatesDialog({
  open,
  onClose,
  exportType = "excel", // "excel" | "pdf"
  locations = [],
  candidates = [],
  defaultLocation = "",
}) {
  const [selectedLocation, setSelectedLocation] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [filteredCandidates, setFilteredCandidates] = useState([]);

  const exportContainerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSelectedLocation(defaultLocation || "");
      setFromDate("");
      setToDate("");
      setErrorMessage("");
      setLoading(false);
      setFilteredCandidates([]);
    }
  }, [open, defaultLocation]);

  // Validation
  const isValidDateRange = () => {
    if (fromDate && toDate) {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      if (start > end) {
        return false;
      }
    }
    return true;
  };

  const handleDateChange = (type, val) => {
    setErrorMessage("");
    if (type === "from") {
      setFromDate(val);
      if (val && toDate && new Date(val) > new Date(toDate)) {
        setErrorMessage("From Date cannot be greater than To Date.");
      }
    } else {
      setToDate(val);
      if (fromDate && val && new Date(fromDate) > new Date(val)) {
        setErrorMessage("From Date cannot be greater than To Date.");
      }
    }
  };

  const filterCandidatesList = (sourceList) => {
    if (!Array.isArray(sourceList)) return [];

    return sourceList.filter((c) => {
      // Location Filter
      if (
        selectedLocation &&
        selectedLocation !== "ALL" &&
        selectedLocation !== "All Locations" &&
        selectedLocation.trim() !== ""
      ) {
        if (c.location !== selectedLocation) {
          return false;
        }
      }

      // Date Filter (inclusive)
      if (fromDate || toDate) {
        const dateVal =
          c.createdAt || c.registrationDate || c.created_at || c.assessmentDate;
        if (!dateVal) return false;

        const candDate = new Date(dateVal);
        if (isNaN(candDate.getTime())) return false;

        if (fromDate) {
          const start = new Date(fromDate + "T00:00:00");
          if (candDate < start) return false;
        }

        if (toDate) {
          const end = new Date(toDate + "T23:59:59.999");
          if (candDate > end) return false;
        }
      }

      return true;
    });
  };

  const handleExport = async () => {
    if (!isValidDateRange()) {
      setErrorMessage("From Date cannot be greater than To Date.");
      return;
    }

    setErrorMessage("");
    setLoading(true);

    try {
      // 🔑 Fetch ALL candidates matching the query (limit=10000) so export is NOT restricted by frontend table pagination (10 per page)
      let allData = candidates;
      try {
        const locQuery =
          selectedLocation &&
          selectedLocation !== "ALL" &&
          selectedLocation !== "All Locations"
            ? selectedLocation
            : "";
        const res = await getAllCandidates(1, 10000, locQuery, "");
        if (res?.data?.data && Array.isArray(res.data.data)) {
          allData = res.data.data;
        }
      } catch (e) {
        console.warn("Falling back to loaded candidate data for export", e);
      }

      const filtered = filterCandidatesList(allData);

      if (!filtered || filtered.length === 0) {
        setErrorMessage("No candidates found for the selected filters.");
        setLoading(false);
        return;
      }

      if (exportType === "excel") {
        exportExcel(filtered);
        toast.success(`Successfully exported ${filtered.length} candidate(s) to Excel`);
        setLoading(false);
        onClose();
      } else {
        // PDF Export: Render 3-page templates in DOM for all filtered candidates
        setFilteredCandidates(filtered);
        setTimeout(async () => {
          try {
            await generateMultiCandidatePdf(filtered);
            toast.success(`Successfully exported 3-page report PDF for ${filtered.length} candidate(s)`);
            onClose();
          } catch (err) {
            console.error("PDF Export error:", err);
            setErrorMessage("Failed to generate PDF. Please try again.");
          } finally {
            setLoading(false);
          }
        }, 300);
      }
    } catch (err) {
      console.error("Export error:", err);
      setErrorMessage("Failed to export candidates. Please try again.");
      setLoading(false);
    }
  };

  const exportExcel = (filteredData) => {
    const exportRows = filteredData.map((cand, index) => {
      const aadharDocUrl = getFileUrl(cand.aadharFile);
      const aadharBackDocUrl = getFileUrl(cand.aadharBackFile);
      const dlDocUrl = getFileUrl(cand.dlFile);
      const dlBackDocUrl = getFileUrl(cand.dlBackFile);
      const otherDocUrl = getFileUrl(cand.otherFile);

      return {
        "S.No": index + 1,
        "Candidate ID": cand._id || "—",
        "Candidate Name": cand.name ? cand.name.toUpperCase() : "—",
        "Father's Name": cand.fathersName || "—",
        "Mother's Name": cand.mothersName || "—",
        "Date of Birth": cand.dob
          ? new Date(cand.dob).toLocaleDateString("en-IN")
          : "—",
        "Gender": cand.gender || "—",
        "Marital Status": cand.maritalStatus || "—",
        "Religion": cand.religion || "—",
        "Caste": cand.caste || "—",
        "Annual Income": cand.annualIncome || "—",
        "Mobile Number": cand.contactNumber || "—",
        "Qualification": cand.qualification || "—",
        "Experience (Years)": cand.experienceYears || "—",
        "Present Employer": cand.employerName || "—",
        "Employer Address": cand.employerAddress || "—",
        "Location": cand.location || "—",
        "State": cand.state || "—",
        "Branch": cand.branch?.name || cand.branch || "—",
        "Project": cand.project?.name || cand.project || "—",
        "Program": cand.program?.name || cand.program || "—",
        "Batch ID": cand.batchId || "—",
        "Aadhaar Number": cand.aadharNumber || "—",
        "Aadhaar Front Document Link": aadharDocUrl || "No Document Uploaded",
        "Aadhaar Back Document Link": aadharBackDocUrl || "No Document Uploaded",
        "DL Number": cand.dlNo || "—",
        "DL Issue Date": cand.dlIssueDate
          ? new Date(cand.dlIssueDate).toLocaleDateString("en-IN")
          : "—",
        "DL Expiry Date": cand.licenseExpiryDate
          ? new Date(cand.licenseExpiryDate).toLocaleDateString("en-IN")
          : "—",
        "DL Type": cand.dlType || "—",
        "DL Authority": cand.dlAuthority || "—",
        "Driving Licence Front Document Link": dlDocUrl || "No Document Uploaded",
        "Driving Licence Back Document Link": dlBackDocUrl || "No Document Uploaded",
        "Other Document Link": otherDocUrl || "No Document Uploaded",
        "Address": cand.address || "—",
        "Enrolled By": cand.enrolledBy || "—",
        "Biometric Status": cand.biometricFace ? "Enrolled" : "Not Enrolled",
        "Face Match Score": cand.faceMatchScore != null ? `${cand.faceMatchScore}%` : "—",
        "Face Verified": cand.faceVerified ? "Yes" : "No",
        "Approval Status": cand.approvalstatus || "Pending",
        "Remarks": cand.remarks || cand.approvalReason || "—",
        "Created Date": cand.createdAt
          ? new Date(cand.createdAt).toLocaleString("en-IN")
          : "—",
        "Verified Date": cand.verifiedAt
          ? new Date(cand.verifiedAt).toLocaleString("en-IN")
          : "—",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);

    const colWidths = Object.keys(exportRows[0] || {}).map((key) => {
      let maxLen = key.length;
      exportRows.forEach((row) => {
        const valStr = row[key] ? String(row[key]) : "";
        if (valStr.length > maxLen) maxLen = valStr.length;
      });
      return { wch: Math.min(Math.max(maxLen + 3, 10), 40) };
    });
    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");

    const filename = getExportFilename(
      selectedLocation,
      fromDate,
      toDate,
      "xlsx"
    );
    XLSX.writeFile(workbook, filename);
  };

  const generateMultiCandidatePdf = async (filteredData) => {
    if (!exportContainerRef.current) {
      throw new Error("Export container not mounted");
    }

    // 1. Wait for images to load inside export container
    const images = exportContainerRef.current.querySelectorAll("img");
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );

    // 2. Query all .pdf-page elements (3 pages per candidate)
    const pageElements = exportContainerRef.current.querySelectorAll(".pdf-page");
    if (!pageElements || pageElements.length === 0) {
      throw new Error("No PDF page elements found.");
    }

    // 3. Initialize jsPDF (A4 portrait: 210mm x 297mm)
    const pdf = new jsPDF("portrait", "mm", "a4");

    // 4. Capture each 3-page set per candidate
    for (let i = 0; i < pageElements.length; i++) {
      const pageEl = pageElements[i];
      const canvas = await html2canvas(pageEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 794,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      if (i > 0) {
        pdf.addPage("a4", "portrait");
      }
      pdf.addImage(imgData, "JPEG", 0, 0, 210, 297);
    }

    // 5. Save single PDF containing all 3-page candidate reports
    const filename = getExportFilename(
      selectedLocation,
      fromDate,
      toDate,
      "pdf"
    );
    pdf.save(filename);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? null : onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            p: 0.5,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: 18,
            color: "#0f172a",
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Export Candidates</span>
          <span
            className={`ml-auto text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
              exportType === "pdf"
                ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
            }`}
          >
            {exportType === "pdf" ? "PDF Export (3-Page Report)" : "Excel Export"}
          </span>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
            {errorMessage && (
              <Alert severity="error" sx={{ borderRadius: "10px", fontSize: 13 }}>
                {errorMessage}
              </Alert>
            )}

            {/* LOCATION SELECT */}
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 600, mb: 0.5, display: "block" }}
              >
                Location
              </Typography>
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setErrorMessage("");
                  setSelectedLocation(e.target.value);
                }}
                disabled={loading}
                className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-[13.5px] text-slate-800 shadow-[0_1px_2px_rgba(15,23,42,0.03)] outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/15 cursor-pointer"
              >
                <option value="">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </Box>

            {/* FROM DATE */}
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 600, mb: 0.5, display: "block" }}
              >
                From Date
              </Typography>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => handleDateChange("from", e.target.value)}
                disabled={loading}
                className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-[13.5px] text-slate-800 shadow-[0_1px_2px_rgba(15,23,42,0.03)] outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/15"
              />
            </Box>

            {/* TO DATE */}
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 600, mb: 0.5, display: "block" }}
              >
                To Date
              </Typography>
              <input
                type="date"
                value={toDate}
                onChange={(e) => handleDateChange("to", e.target.value)}
                disabled={loading}
                className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-[13.5px] text-slate-800 shadow-[0_1px_2px_rgba(15,23,42,0.03)] outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/15"
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "#64748b",
              fontWeight: 600,
              borderRadius: "10px",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleExport}
            disabled={loading || Boolean(errorMessage && errorMessage.includes("greater"))}
            sx={{
              textTransform: "none",
              px: 3,
              py: 1,
              borderRadius: "10px",
              fontWeight: 600,
              boxShadow: "none",
              backgroundColor: exportType === "pdf" ? "#4f46e5" : "#059669",
              "&:hover": {
                backgroundColor: exportType === "pdf" ? "#4338ca" : "#047857",
              },
            }}
          >
            {loading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} color="inherit" />
                <span>Generating...</span>
              </Box>
            ) : exportType === "pdf" ? (
              "Export PDF"
            ) : (
              "Export Excel"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= HIDDEN PRINTABLE CONTAINER FOR 3-PAGE PDF GENERATION ================= */}
      <div
        ref={exportContainerRef}
        style={{
          position: "absolute",
          left: "-99999px",
          top: 0,
          width: "210mm",
          backgroundColor: "#ffffff",
        }}
      >
        {filteredCandidates.map((selectedCandidate, candIdx) => {
          const govFieldOrder = [
            { label: "Name of the Candidate", value: selectedCandidate.name },
            { label: "Father's Name", value: selectedCandidate.fathersName },
            { label: "Mother's Name", value: selectedCandidate.mothersName },
            {
              label: "Date of Birth",
              value: selectedCandidate.dob ? formatDateDisplay(selectedCandidate.dob) : null,
            },
            { label: "Aadhaar No", value: selectedCandidate.aadharNumber },
            { label: "Marital Status", value: selectedCandidate.maritalStatus },
            { label: "Religion", value: selectedCandidate.religion },
            { label: "Caste", value: selectedCandidate.caste },
            { label: "Annual Income", value: selectedCandidate.annualIncome },
            { label: "Address", value: selectedCandidate.address },
            {
              label: "Driving License No & Issue Date",
              value: selectedCandidate.dlNo
                ? `${selectedCandidate.dlNo}${
                    selectedCandidate.dlIssueDate
                      ? "  |  " + formatDateDisplay(selectedCandidate.dlIssueDate)
                      : ""
                  }`
                : "",
            },
            { label: "Type of Driving License", value: selectedCandidate.dlType },
            { label: "D.L Issuing Authority", value: selectedCandidate.dlAuthority },
            { label: "Mobile Number", value: selectedCandidate.contactNumber },
            { label: "Qualification", value: selectedCandidate.qualification },
            { label: "Experience in Years", value: selectedCandidate.experienceYears },
            { label: "Present Employer Name", value: selectedCandidate.employerName },
            { label: "Employer Address & Contact No", value: selectedCandidate.employerAddress },
            { label: "Enrolled by Admin Name", value: selectedCandidate.enrolledBy },
            { label: "Reference ID", value: selectedCandidate.referenceId || selectedCandidate._id },
          ];

          const aadharImg =
            (selectedCandidate.aadharFace
              ? getFileUrl(selectedCandidate.aadharFace)
              : null) ||
            localStorage.getItem(`dbskills_aadhar_face_${selectedCandidate._id}`) ||
            (selectedCandidate.aadharFile &&
            /\.(jpg|jpeg|png|webp)$/i.test(selectedCandidate.aadharFile)
              ? getFileUrl(selectedCandidate.aadharFile)
              : null);

          const dlImg =
            (selectedCandidate.dlFace
              ? getFileUrl(selectedCandidate.dlFace)
              : null) ||
            localStorage.getItem(`dbskills_dl_face_${selectedCandidate._id}`) ||
            (selectedCandidate.dlFile &&
            /\.(jpg|jpeg|png|webp)$/i.test(selectedCandidate.dlFile)
              ? getFileUrl(selectedCandidate.dlFile)
              : null);

          const bioImg =
            selectedCandidate.biometricFace ||
            localStorage.getItem(`dbskills_face_${selectedCandidate._id}`);

          const photoSrc = bioImg || aadharImg || dlImg || "/logos/placeholder.png";

          const topFields = govFieldOrder.slice(0, 8);
          const bottomFields = govFieldOrder.slice(8);

          return (
            <React.Fragment key={selectedCandidate._id || candIdx}>
              {/* PAGE 1: ENROLLMENT FORM SHEET */}
              <div
                className="pdf-page"
                style={{
                  width: "210mm",
                  minHeight: "297mm",
                  height: "297mm",
                  background: "#ffffff",
                  padding: "15mm",
                  boxSizing: "border-box",
                  position: "relative",
                  overflow: "hidden",
                  fontFamily: "Arial, Helvetica, sans-serif",
                  color: "#111",
                }}
              >
                {/* LOGOS ROW */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "16px",
                  }}
                >
                  <div style={{ width: "33%", display: "flex", justifyContent: "flex-start" }}>
                    <img
                      src={dbskillsLogo}
                      alt="DBSkills"
                      crossOrigin="anonymous"
                      style={{ height: "65px", objectFit: "contain" }}
                    />
                  </div>
                  <div style={{ width: "33%", display: "flex", justifyContent: "center" }}>
                    <img
                      src={skillIndiaLogo}
                      alt="Skill India"
                      crossOrigin="anonymous"
                      style={{ height: "60px", objectFit: "contain" }}
                    />
                  </div>
                  <div style={{ width: "33%", display: "flex", justifyContent: "flex-end" }}>
                    <img
                      src={shriramFinanceLogo}
                      alt="Shriram Finance"
                      crossOrigin="anonymous"
                      style={{ height: "42px", objectFit: "contain" }}
                    />
                  </div>
                </div>

                {/* TITLE & SUBTITLE */}
                <h1
                  style={{
                    fontSize: "18px",
                    fontWeight: "800",
                    textAlign: "center",
                    margin: "0 0 4px",
                    letterSpacing: "0.3px",
                  }}
                >
                  REFRESHER AND CERTIFICATION PROGRAM
                </h1>
                <h2
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    textAlign: "center",
                    textDecoration: "underline",
                    margin: "0 0 12px",
                    color: "#1e293b",
                  }}
                >
                  CANDIDATE ENROLLMENT SHEET
                </h2>

                {/* LOCATION / STATE / DATE META LINE */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "11px",
                    fontWeight: "700",
                    borderTop: "1px solid #222",
                    borderBottom: "1px solid #222",
                    padding: "6px 8px",
                    marginBottom: "10px",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <span>LOCATION: {selectedCandidate.location || "—"}</span>
                  <span>STATE: {selectedCandidate.state || "—"}</span>
                  <span>
                    DATE:{" "}
                    {selectedCandidate.createdAt
                      ? formatDateDisplay(selectedCandidate.createdAt)
                      : "—"}
                  </span>
                </div>

                {/* TOP FIELDS & PHOTO SIDE-BY-SIDE */}
                <div
                  style={{
                    display: "flex",
                    gap: "24px",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {topFields.map(({ label, value }) => (
                      <div
                        key={label}
                        style={{
                          display: "flex",
                          fontSize: "11px",
                          padding: "3.5px 0",
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <span
                          style={{
                            width: "180px",
                            flexShrink: 0,
                            fontWeight: "700",
                            textTransform: "uppercase",
                            color: "#334155",
                          }}
                        >
                          {label}
                        </span>
                        <span style={{ width: "12px", flexShrink: 0, fontWeight: "700", color: "#94a3b8" }}>
                          :
                        </span>
                        <span style={{ flex: 1, fontWeight: "600", color: "#0f172a" }}>
                          {value || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div style={{ flexShrink: 0, marginTop: "4px" }}>
                    <img
                      src={photoSrc}
                      alt="Candidate"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        e.currentTarget.src = "/logos/placeholder.png";
                      }}
                      style={{
                        width: "35mm",
                        height: "45mm",
                        objectFit: "cover",
                        border: "1px solid #222",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>
                </div>

                {/* BOTTOM FIELDS FULL WIDTH */}
                <div style={{ marginBottom: "10px" }}>
                  {bottomFields.map(({ label, value }) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        fontSize: "11px",
                        padding: "3.5px 0",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      <span
                        style={{
                          width: "210px",
                          flexShrink: 0,
                          fontWeight: "700",
                          textTransform: "uppercase",
                          color: "#334155",
                        }}
                      >
                        {label}
                      </span>
                      <span style={{ width: "12px", flexShrink: 0, fontWeight: "700", color: "#94a3b8" }}>
                        :
                      </span>
                      <span style={{ flex: 1, fontWeight: "600", color: "#0f172a" }}>
                        {value || "-"}
                      </span>
                    </div>
                  ))}
                </div>

                {/* DECLARATION */}
                <div
                  style={{
                    marginTop: "12px",
                    fontSize: "10.5px",
                    lineHeight: "1.4",
                    textAlign: "justify",
                    color: "#334155",
                  }}
                >
                  <p style={{ fontWeight: "800", fontSize: "11px", marginBottom: "2px", color: "#0f172a" }}>
                    DECLARATION
                  </p>
                  <p style={{ margin: 0 }}>
                    I do hereby declare that I have given my consent to attend the
                    refresher and certification program sponsored by Dbskills and I have
                    not paid any amount for this program. I also declare that I have
                    given my Aadhaar copy and driving license copy along with this form
                    which is to be used for training and certification purpose.
                  </p>
                </div>

                {/* SIGNATURE BLOCK */}
                <div
                  style={{
                    marginTop: "16px",
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#1e293b",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    paddingTop: "8px",
                    borderTop: "1px solid #cbd5e1",
                  }}
                >
                  <div>
                    <div>Place: {selectedCandidate.location || "—"}</div>
                    <div style={{ marginTop: "3px" }}>
                      Date:{" "}
                      {selectedCandidate.createdAt
                        ? formatDateDisplay(selectedCandidate.createdAt)
                        : "—"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        width: "120px",
                        borderBottom: "1px solid #94a3b8",
                        marginBottom: "4px",
                      }}
                    ></div>
                    <div style={{ fontSize: "9.5px", color: "#64748b", textTransform: "uppercase" }}>
                      Signature of Candidate
                    </div>
                  </div>
                </div>

                {/* FOOTER */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "12mm",
                    left: "15mm",
                    right: "15mm",
                    borderTop: "1px solid #cbd5e1",
                    paddingTop: "5px",
                    fontSize: "9.5px",
                    color: "#64748b",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>DBSkill · Candidate #{candIdx + 1} · Page 1 of 3 (Enrollment Sheet)</span>
                  <span>Confidential Report</span>
                </div>
              </div>

              {/* PAGE 2: AADHAAR DOCUMENT */}
              <div
                className="pdf-page"
                style={{
                  width: "210mm",
                  minHeight: "297mm",
                  height: "297mm",
                  background: "#ffffff",
                  padding: "15mm",
                  boxSizing: "border-box",
                  position: "relative",
                  overflow: "hidden",
                  fontFamily: "Arial, Helvetica, sans-serif",
                  color: "#111",
                }}
              >
                <h2
                  style={{
                    textAlign: "center",
                    fontSize: "20px",
                    marginBottom: "20px",
                    fontWeight: "800",
                    color: "#0f172a",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Aadhaar Document
                </h2>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "11px",
                    fontWeight: "700",
                    padding: "6px 8px",
                    marginBottom: "16px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                  }}
                >
                  <span>Candidate: {selectedCandidate.name ? selectedCandidate.name.toUpperCase() : "—"}</span>
                  <span>Aadhaar No: {selectedCandidate.aadharNumber || "—"}</span>
                </div>

                {!selectedCandidate?.aadharFile && !selectedCandidate?.aadharBackFile ? (
                  <div
                    style={{
                      padding: "50px",
                      border: "1px dashed #cbd5e1",
                      borderRadius: "12px",
                      backgroundColor: "#f8fafc",
                      textAlign: "center",
                      marginTop: "40px",
                    }}
                  >
                    <p style={{ color: "#64748b", fontSize: "15px", fontStyle: "italic", margin: 0 }}>
                      No Aadhaar Document Uploaded
                    </p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", height: "205mm", justifyContent: "center", alignItems: "center" }}>
                    {selectedCandidate?.aadharFile && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        {selectedCandidate?.aadharBackFile && (
                          <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", marginBottom: "2px", textTransform: "uppercase" }}>Front Side</span>
                        )}
                        {/\.(jpg|jpeg|png|webp)$/i.test(selectedCandidate.aadharFile) ? (
                          <img
                            src={getFileUrl(selectedCandidate.aadharFile)}
                            alt="Aadhaar Front"
                            crossOrigin="anonymous"
                            style={{
                              maxWidth: "100%",
                              maxHeight: selectedCandidate?.aadharBackFile ? "85mm" : "190mm",
                              objectFit: "contain",
                              borderRadius: "4px",
                              border: "1px solid #e2e8f0",
                            }}
                          />
                        ) : (
                          <div style={{ padding: "15px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#f8fafc", width: "80%", textAlign: "center" }}>
                            <p style={{ fontSize: "11px", color: "#64748b" }}>{selectedCandidate.aadharFile.split("/").pop()}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {selectedCandidate?.aadharBackFile && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", marginBottom: "2px", textTransform: "uppercase" }}>Back Side</span>
                        {/\.(jpg|jpeg|png|webp)$/i.test(selectedCandidate.aadharBackFile) ? (
                          <img
                            src={getFileUrl(selectedCandidate.aadharBackFile)}
                            alt="Aadhaar Back"
                            crossOrigin="anonymous"
                            style={{
                              maxWidth: "100%",
                              maxHeight: selectedCandidate?.aadharFile ? "85mm" : "190mm",
                              objectFit: "contain",
                              borderRadius: "4px",
                              border: "1px solid #e2e8f0",
                            }}
                          />
                        ) : (
                          <div style={{ padding: "15px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#f8fafc", width: "80%", textAlign: "center" }}>
                            <p style={{ fontSize: "11px", color: "#64748b" }}>{selectedCandidate.aadharBackFile.split("/").pop()}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* FOOTER */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "12mm",
                    left: "15mm",
                    right: "15mm",
                    borderTop: "1px solid #cbd5e1",
                    paddingTop: "5px",
                    fontSize: "9.5px",
                    color: "#64748b",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>DBSkill · Candidate #{candIdx + 1} · Page 2 of 3 (Aadhaar Document)</span>
                  <span>Confidential Attachment</span>
                </div>
              </div>

              {/* PAGE 3: DRIVING LICENCE DOCUMENT */}
              <div
                className="pdf-page"
                style={{
                  width: "210mm",
                  minHeight: "297mm",
                  height: "297mm",
                  background: "#ffffff",
                  padding: "15mm",
                  boxSizing: "border-box",
                  position: "relative",
                  overflow: "hidden",
                  fontFamily: "Arial, Helvetica, sans-serif",
                  color: "#111",
                }}
              >
                <h2
                  style={{
                    textAlign: "center",
                    fontSize: "20px",
                    marginBottom: "20px",
                    fontWeight: "800",
                    color: "#0f172a",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Driving Licence
                </h2>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "11px",
                    fontWeight: "700",
                    padding: "6px 8px",
                    marginBottom: "16px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                  }}
                >
                  <span>Candidate: {selectedCandidate.name ? selectedCandidate.name.toUpperCase() : "—"}</span>
                  <span>DL No: {selectedCandidate.dlNo || "—"}</span>
                </div>

                {!selectedCandidate?.dlFile && !selectedCandidate?.dlBackFile ? (
                  <div
                    style={{
                      padding: "50px",
                      border: "1px dashed #cbd5e1",
                      borderRadius: "12px",
                      backgroundColor: "#f8fafc",
                      textAlign: "center",
                      marginTop: "40px",
                    }}
                  >
                    <p style={{ color: "#64748b", fontSize: "15px", fontStyle: "italic", margin: 0 }}>
                      No Driving Licence Uploaded
                    </p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", height: "205mm", justifyContent: "center", alignItems: "center" }}>
                    {selectedCandidate?.dlFile && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        {selectedCandidate?.dlBackFile && (
                          <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", marginBottom: "2px", textTransform: "uppercase" }}>Front Side</span>
                        )}
                        {/\.(jpg|jpeg|png|webp)$/i.test(selectedCandidate.dlFile) ? (
                          <img
                            src={getFileUrl(selectedCandidate.dlFile)}
                            alt="Driving Licence Front"
                            crossOrigin="anonymous"
                            style={{
                              maxWidth: "100%",
                              maxHeight: selectedCandidate?.dlBackFile ? "85mm" : "190mm",
                              objectFit: "contain",
                              borderRadius: "4px",
                              border: "1px solid #e2e8f0",
                            }}
                          />
                        ) : (
                          <div style={{ padding: "15px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#f8fafc", width: "80%", textAlign: "center" }}>
                            <p style={{ fontSize: "11px", color: "#64748b" }}>{selectedCandidate.dlFile.split("/").pop()}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {selectedCandidate?.dlBackFile && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        <span style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", marginBottom: "2px", textTransform: "uppercase" }}>Back Side</span>
                        {/\.(jpg|jpeg|png|webp)$/i.test(selectedCandidate.dlBackFile) ? (
                          <img
                            src={getFileUrl(selectedCandidate.dlBackFile)}
                            alt="Driving Licence Back"
                            crossOrigin="anonymous"
                            style={{
                              maxWidth: "100%",
                              maxHeight: selectedCandidate?.dlFile ? "85mm" : "190mm",
                              objectFit: "contain",
                              borderRadius: "4px",
                              border: "1px solid #e2e8f0",
                            }}
                          />
                        ) : (
                          <div style={{ padding: "15px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#f8fafc", width: "80%", textAlign: "center" }}>
                            <p style={{ fontSize: "11px", color: "#64748b" }}>{selectedCandidate.dlBackFile.split("/").pop()}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* FOOTER */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "12mm",
                    left: "15mm",
                    right: "15mm",
                    borderTop: "1px solid #cbd5e1",
                    paddingTop: "5px",
                    fontSize: "9.5px",
                    color: "#64748b",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>DBSkill · Candidate #{candIdx + 1} · Page 3 of 3 (Driving Licence Document)</span>
                  <span>Confidential Attachment</span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}
